import type { WrittenPage } from '../../sections'
import { EBOOKS, EBOOKS_INDEX, ebookHref } from '../../ebooks'
import { REPORTS, REPORTS_INDEX, reportHref } from '../../reports'

/**
 * Impact Labs hub sayfası, Türkçesi.
 *
 * Bölüm sırası, kartlar ve bağlantılar İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. Rapor ve e-book başlıkları katalogdan geliyor, o
 * yüzden İngilizce kalıyor — yayınlar o adla yayımlandı.
 */
const report = (slug: string) => {
  const r = REPORTS.find((x) => x.slug === slug)
  return { title: r?.title ?? slug, href: reportHref('en', slug), cover: r?.cover }
}

const ebook = (slug: string) => {
  const b = EBOOKS.find((x) => x.slug === slug)
  return {
    title: b?.title ?? slug,
    href: b ? ebookHref('en', b) : `/en/${EBOOKS_INDEX}`,
    cover: b ? { src: b.cover.src, alt: b.cover.alt } : undefined,
  }
}

const HEPSI = 'Tümünü Keşfedin'

export const impactLabs: WrittenPage = {
  navLabel: 'Impact Labs',
  title: 'Impact Labs',
  description:
    'Dijital ve yapay zeka dönüşümü üzerine raporlar, e-bookʼlar, yazılar ve etkinlikler: sektör trendleri, gelecek senaryoları ve benchmarkʼlar.',
  sections: [
    {
      kind: 'covers',
      heading: 'Raporlar',
      sub: 'Sektör trendlerini keşfedin, gelecek senaryolarını görün, benchmarkʼları öğrenin.',
      link: { label: HEPSI, href: `/en/${REPORTS_INDEX}` },
      cta: { label: HEPSI, href: `/en/${REPORTS_INDEX}` },
      items: [
        report('digital-ai-maturity-report-2026'),
        report('digital-ai-maturity-report-usa-edition'),
        report('turkiye-dijital-ve-yapay-zeka-olgunluk-raporu-2026'),
        report('digital-and-ai-maturity-report-2026-airports-industry'),
      ],
    },
    {
      kind: 'covers',
      heading: 'Ebooks',
      sub: 'Dijital dönüşüm, yapay zeka ve sürdürülebilirlik üzerine kapsamlı rehberlere ulaşın.',
      link: { label: HEPSI, href: `/en/${EBOOKS_INDEX}` },
      cta: { label: HEPSI, href: `/en/${EBOOKS_INDEX}` },
      items: [
        ebook('agentic-ai'),
        ebook('mastering-communication-for-transformation'),
        ebook('importance-of-measurement-for-transformation-success'),
        ebook('ai-grocery-shopping'),
      ],
    },
    {
      kind: 'covers',
      heading: 'Blog',
      sub: 'Dönüşümü derinlemesine anlatımlar ve gerçek hikâyelerle daha yakından tanıyın.',
      link: { label: HEPSI, href: '/en/blog' },
      cta: { label: HEPSI, href: '/en/blog' },
      items: [
        {
          title:
            'From 1.9 to 4.2: Why Transformation Maturity Separates Laggards, Competitors, and Leaders',
          note: 'Dönüşüm olgunluğu, gelecekteki rekabetin belirleyicisi hâline geliyor. Dağınık ilerleyen kurumlarda…',
          href: '/en/blog',
          cover: {
            src: '/photo/blog-maturity-1-9-to-4-2.webp',
            alt: 'Blog kapağı: 1.9 vs 3.1 vs 4.2 — dönüşüm olgunluğunun geride kalanları, rakipleri ve liderleri nasıl ayırdığı',
          },
        },
        {
          title:
            'CIO AI Transformation Blog Series – 1. Master Your AI Transformation: Why CIOs Must Lead Enterprise AI',
          note: 'Yapay zeka dönüşümü pilotlardan ve araçlardan ibaret değil. CIOʼlar dağınık denemelerden…',
          href: '/en/blog',
          cover: {
            src: '/photo/blog-cio-ai-transformation-01.webp',
            alt: 'Blog kapağı: CIO AI Transformation Blog Series 01 — yapay zeka dönüşümünde CIOʼların kurumsal AIʼı neden yönetmesi gerektiği',
          },
        },
        {
          title:
            'CIO AI Transformation Blog Series – 2. How Ready Is Your Organization for AI Transformation?',
          note: 'Yapay zeka dönüşümü hazırlıkla başlar. CIOʼların olgunluğu ölçecek, açıkları görünür kılacak…',
          href: '/en/blog',
          cover: {
            src: '/photo/blog-cio-ai-transformation-02.webp',
            alt: 'Blog kapağı: CIO AI Transformation Blog Series 02 — kurumunuz yapay zeka dönüşümüne ne kadar hazır',
          },
        },
        {
          title: 'Transformation Measurement: 7 Proven Ways to Stop Waste',
          note: 'Dönüşümü hırs başlatır, ölçüm sürdürür. İşte bunu sağlayan yedi ilke…',
          href: '/en/blog',
          cover: {
            src: '/photo/blog-transformation-measurement.webp',
            alt: 'Blog kapağı: yükselen sütunların arasındaki aydınlık yolda ilerleyen ekip — olgunluk skorunun kurumsal yetkinlikler boyunca izlenmesi',
          },
        },
      ],
    },
    {
      kind: 'split',
      heading: 'Sertifikanızı Alın, Öğrenmeye Devam Edenlere Katılın',
      paragraphs: [
        'Digital & AI Maturity Index Course',
        'Dijital ve yapay zeka olgunluğunda pratik bilgi edinin, alanınızda güvenilir bir danışman olarak konumlanın.',
      ],
      photo: {
        src: '/photo/learning-platform-tablet.webp',
        alt: 'Digitopia Learning Platform giriş ekranı, tablet üzerinde',
      },
      cta: { label: 'Daha fazlası', href: '/en/digital-ai-maturity-index-daimi-course' },
    },
    {
      kind: 'covers',
      heading: 'Geçmiş Etkinlikler',
      sub: 'Dönüşümün şifreleri, pratik ipuçları ve temel stratejilerle birlikte.',
      link: { label: HEPSI, href: '/en/impact-labs/events' },
      cta: { label: HEPSI, href: '/en/impact-labs/events' },
      items: [
        {
          title: 'Digitopia at BFSI Nexus Conference 2026',
          note: '4 – 5 Mayıs 2026 – The Westin Jersey City Newport, NJ',
          href: '/en/impact-labs/events',
        },
        {
          title: 'Digitopia at AI Next Las Vegas 2026',
          note: '21 – 22 Mayıs 2026 – Horseshoe Las Vegas, NV',
          href: '/en/impact-labs/events',
        },
        {
          title: 'AI Executive Masterclass – USA',
          note: '6 Mayıs, Jersey City | 15 Mayıs, Orlando | 23 Mayıs, Las Vegas',
          href: '/en/impact-labs/events/ai-executive-masterclass',
        },
        {
          title: 'Dönüşümün Yapay Zeka Hali: Digitopia 2.0',
          note: '7 Nisan 2026 | 13:00 – 17:00 – Grand Hyatt Istanbul',
          href: '/en/impact-labs/events',
        },
      ],
    },
  ],
}
